import { text, integer, real, index } from "drizzle-orm/sqlite-core";
import { defineTable } from "@kubdee/sdk/schema";
import { marketingPostTargets } from "./post-targets";

// ============================================================================
// marketing_post_insights — Engagement metrics snapshot per target
//
// One row per `(target_id, provider_post_id, snapshotted_at)`. Polled periodically by the
// connectors module's insights sync job (#791) for each successfully
// published target. Older snapshots stay around so the analytics UI can
// chart engagement over time.
//
// Read-only — only the sync job inserts rows. The doctype registry marks
// this as `isReadOnly: true`.
//
// Metric columns are nullable: providers commonly omit fields when a post is
// too new, a permission scope is missing for that metric, or the network does
// not expose it. `NULL` means "unavailable"; `0` means the provider explicitly
// returned zero.
//
// `engagement_rate` is stored pre-computed (0..1) so list / chart queries
// don't need joins or arithmetic; the formula is provider-specific and
// owned by the connector adapter that fetches the raw metrics.
// ============================================================================
export const marketingPostInsights = defineTable(
  "marketing",
  "post_insights",
  {
    id: text("id").primaryKey(), // ULID
    companyId: text("company_id").notNull(),
    targetId: text("target_id")
      .notNull()
      .references(() => marketingPostTargets.id, { onDelete: "cascade" }),
    providerPostId: text("provider_post_id"),
    snapshottedAt: text("snapshotted_at").notNull(),
    impressions: integer("impressions"),
    reach: integer("reach"),
    likes: integer("likes"),
    comments: integer("comments"),
    shares: integer("shares"),
    clicks: integer("clicks"),
    engagementRate: real("engagement_rate"),
    // Full provider response JSON for forward-compat — new metrics can be
    // surfaced without a schema change.
    rawPayload: text("raw_payload"),
    createdAt: text("created_at").notNull(),
  },
  (table) => [
    index("marketing_post_insights_company_id_idx").on(table.companyId),
    index("marketing_post_insights_target_snapshot_idx").on(
      table.targetId,
      table.snapshottedAt,
    ),
    index("marketing_post_insights_target_provider_snapshot_idx").on(
      table.targetId,
      table.providerPostId,
      table.snapshottedAt,
    ),
  ],
);

export type MarketingPostInsight = typeof marketingPostInsights.$inferSelect;
export type NewMarketingPostInsight = typeof marketingPostInsights.$inferInsert;
